import axios from "axios";

// Detect environment and adjust API URL accordingly
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  // If environment URL is set, use it (highest priority)
  if (envUrl) {
    return envUrl;
  }

  // Use UrbanPOS API for all environments (development and production)
  return "https://api.urbanpos.com/api";
};

const API_BASE_URL = getApiBaseUrl();

console.log("🌐 API Base URL:", API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000, // 10 second timeout for mobile networks
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    // Update token timestamp on every successful API call
    authService.updateTokenTimestamp();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      // Try to refresh session with a lightweight API call
      try {
        await api.get("/auth/verify");
        // Update token timestamp on successful refresh
        authService.updateTokenTimestamp();
        return api(originalRequest);
      } catch (refreshError) {
        // If refresh fails, force logout and redirect
        console.log("🚨 Session refresh failed - forcing logout");
        authService.forceLogoutAndRedirect("Session refresh failed");
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const authService = {
  async login(credentials) {
    try {
      console.log("🔐 Attempting login...", {
        url: `${API_BASE_URL}/auth/login`,
        credentials: { ...credentials, password: "[HIDDEN]" },
      });

      const response = await api.post("/auth/login", credentials);

      console.log("✅ Login successful", {
        status: response.status,
        hasToken: !!response.data.token,
        hasUser: !!response.data.user,
      });

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        // Store login timestamp for token lifetime tracking
        localStorage.setItem("tokenTimestamp", Date.now().toString());
        console.log("💾 Token and user data saved with timestamp");
      }
      return response.data;
    } catch (error) {
      console.error("❌ Login failed:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        network: !error.response ? "Network Error" : "Server Error",
      });

      // Provide better error messages for mobile
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        (!error.response
          ? "Network connection failed. Please check your internet connection."
          : "Login failed");

      const errorObj = new Error(errorMessage);
      Object.assign(errorObj, error.response?.data);
      throw errorObj;
    }
  },

  async register(userData) {
    try {
      const response = await api.post("/auth/register", userData);
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: "Registration failed" };
    }
  },

  async getProfile() {
    try {
      const response = await api.get("/user/profile");
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: "Failed to get profile" };
    }
  },

  logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("tokenTimestamp");
    console.log("🚪 Logged out and cleared all auth data");
  },

  // Update token timestamp on successful API calls (keeps session alive)
  updateTokenTimestamp() {
    if (this.isAuthenticated()) {
      localStorage.setItem("tokenTimestamp", Date.now().toString());
    }
  },

  // Check if token is still fresh (for debugging purposes)
  getTokenAge() {
    const timestamp = localStorage.getItem("tokenTimestamp");
    if (timestamp) {
      const ageInMinutes = (Date.now() - parseInt(timestamp)) / (1000 * 60);
      return Math.round(ageInMinutes);
    }
    return null;
  },

  // Keep token alive indefinitely - prevents session expiration
  keepTokenAlive() {
    const token = localStorage.getItem("token");
    if (!token) return false;

    // Reset token timestamp to prevent expiration
    this.updateTokenTimestamp();
    console.log(`🔄 Token kept alive - Age reset to 0 minutes`);

    return true;
  },

  // Override token expiration check - token only expires on manual logout
  isTokenExpired() {
    // Always return false to prevent automatic token expiration
    // Token will only be cleared on manual logout
    const token = localStorage.getItem("token");
    if (!token) return true; // No token means expired

    console.log(`🔐 Token expiration check bypassed - token stays alive`);
    return false;
  },

  // Force logout with immediate redirection (for authentication errors)
  forceLogoutAndRedirect(reason = "Authentication error") {
    console.log(`🚨 Force logout triggered: ${reason}`);

    // Clear all auth data
    this.logout();

    // Immediately redirect to login page
    console.log("🔄 Redirecting to login page...");
    window.location.href = "/login";
  },

  getCurrentUser() {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  },

  getToken() {
    return localStorage.getItem("token");
  },

  isAuthenticated() {
    return !!this.getToken();
  },
};

export default authService;
